import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material/styles';
import { VocabularyTestHistory } from 'app/types';
import { fmt } from 'pages/VocabularyTest/cefr';

interface VocabularyTestProgressCardProps {
  history: VocabularyTestHistory[];
}

const VIEW_BOX_WIDTH = 100;
const VIEW_BOX_HEIGHT = 100;
const Y_TOP = 12;
const Y_BOT = 84;

const VocabularyTestProgressCard: React.FC<VocabularyTestProgressCardProps> = ({
  history,
}) => {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const positive = '#3B6D11';

  const completed = useMemo(
    () => history.filter((h) => h.status === 'DONE'),
    [history],
  );

  const chronological = useMemo(
    () =>
      [...completed].sort(
        (a, b) =>
          dayjs(a.updatedAt).valueOf() - dayjs(b.updatedAt).valueOf(),
      ),
    [completed],
  );

  if (chronological.length < 2) {
    return null;
  }

  // Vocabulary GROWTH is a running total across all tests, built by adding
  // each test's own contribution on top of the previous total (e.g. 2000,
  // then a 5-word test → 2005) — never by diffing one test's `learnedCount`
  // against another's. `learnedCount` is always scoped to that single test
  // (BE counts rows for that test id only), so it's never a shared running
  // scale to subtract across — a small custom test naturally reports a
  // smaller `learnedCount` than a big one before it, and diffing the two
  // would (and did) make the small test's real contribution vanish. Prefer
  // `newlyAddedCount` when the BE has it (excludes words already known from
  // an earlier test, avoiding double-counting); legacy tests without it fall
  // back to their own `learnedCount` added in full.
  const cumulativeCounts: number[] = [];
  chronological.forEach((h, i) => {
    const added =
      typeof h.newlyAddedCount === 'number' ? h.newlyAddedCount : h.learnedCount;
    cumulativeCounts.push(i === 0 ? added : cumulativeCounts[i - 1] + added);
  });

  const minCount = Math.min(...cumulativeCounts);
  const maxCount = Math.max(...cumulativeCounts);
  const range = maxCount - minCount || 1;
  const latest = chronological[chronological.length - 1];
  const earliest = chronological[0];
  const latestValue = cumulativeCounts[cumulativeCounts.length - 1];
  const delta = latestValue - cumulativeCounts[0];
  const months = Math.max(
    0,
    dayjs(latest.updatedAt).diff(dayjs(earliest.updatedAt), 'month'),
  );
  // Bare "DD.MM" is ambiguous once the history spans more than one calendar
  // year (e.g. two December points a year apart look identical) — add a
  // short year suffix only when that's actually the case, so the common
  // single-year history keeps its more compact label.
  const spansMultipleYears =
    dayjs(latest.updatedAt).year() !== dayjs(earliest.updatedAt).year();
  const axisDateFormat = spansMultipleYears ? 'DD.MM.YY' : 'DD.MM';

  const points = chronological.map((h, i) => {
    const x = (i / (chronological.length - 1)) * VIEW_BOX_WIDTH;
    const value = cumulativeCounts[i];
    const y = Y_BOT - ((value - minCount) / range) * (Y_BOT - Y_TOP);
    return { x, y, count: value, date: h.updatedAt, isLast: i === chronological.length - 1 };
  });

  const line = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `0,100 ${line} 100,100`;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        p: { xs: 2, md: '20px 22px' },
      }}
    >
      <Stack
        direction={'row'}
        alignItems={'baseline'}
        justifyContent={'space-between'}
        sx={{ mb: 0.5 }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
          {'Зростання запасу'}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
          {`${chronological.length} тестів`}
        </Typography>
      </Stack>

      <Stack
        direction={'row'}
        alignItems={'baseline'}
        spacing={1.25}
        sx={{ mb: 1.5 }}
      >
        <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>
          {fmt(latestValue)}
        </Typography>
        {delta !== 0 && (
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={0.25}
            sx={{
              color: delta >= 0 ? positive : 'error.main',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            <TrendingUpIcon
              sx={{
                fontSize: 16,
                transform: delta < 0 ? 'rotate(180deg)' : 'none',
              }}
            />
            <Box component={'span'}>
              {`${delta > 0 ? '+' : ''}${fmt(delta)}${
                months > 0 ? ` за ${months} міс` : ''
              }`}
            </Box>
          </Stack>
        )}
      </Stack>

      <Box sx={{ height: 150, position: 'relative' }}>
        <Box
          component={'svg'}
          viewBox={`0 0 ${VIEW_BOX_WIDTH} ${VIEW_BOX_HEIGHT}`}
          preserveAspectRatio={'none'}
          aria-hidden
          sx={{ height: '100%', inset: 0, position: 'absolute', width: '100%' }}
        >
          <defs>
            <linearGradient id={'vtArea'} x1={'0'} y1={'0'} x2={'0'} y2={'1'}>
              <stop offset={'0%'} stopColor={accent} stopOpacity={0.22} />
              <stop offset={'100%'} stopColor={accent} stopOpacity={0} />
            </linearGradient>
          </defs>
          <polygon points={area} fill={'url(#vtArea)'} />
          <polyline
            points={line}
            fill={'none'}
            stroke={accent}
            strokeWidth={2.5}
            strokeLinecap={'round'}
            strokeLinejoin={'round'}
            vectorEffect={'non-scaling-stroke'}
          />
        </Box>
        {points.map((p, i) => (
          <Box
            key={i}
            sx={{
              left: `${p.x}%`,
              position: 'absolute',
              top: `${p.y}%`,
              transform: 'translate(-50%,-50%)',
            }}
          >
            <Box
              sx={{
                bgcolor: accent,
                border: '2px solid',
                borderColor: 'background.paper',
                borderRadius: '50%',
                height: p.isLast ? 11 : 8,
                width: p.isLast ? 11 : 8,
              }}
            />
            {/* Every point gets its cumulative count now, not just the last
                one. Non-last labels stagger between two heights (by index
                parity) so adjacent points' numbers don't run into each
                other; the last point keeps its original bigger, bolder,
                accent-coloured treatment. */}
            <Box
              sx={{
                bottom: p.isLast ? 18 : i % 2 === 0 ? 14 : 24,
                color: p.isLast ? accent : 'text.secondary',
                fontSize: p.isLast ? 12 : 10,
                fontWeight: p.isLast ? 800 : 700,
                left: '50%',
                position: 'absolute',
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
              }}
            >
              {fmt(p.count)}
            </Box>
          </Box>
        ))}
      </Box>

      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        sx={{ mt: 0.75 }}
      >
        {points.map((p, i) => {
          const step = Math.ceil(points.length / 6);
          const show = i % step === 0 || i === points.length - 1;
          return (
            <Typography
              key={i}
              sx={{ color: 'text.secondary', fontSize: 11 }}
            >
              {show ? dayjs(p.date).format(axisDateFormat) : ''}
            </Typography>
          );
        })}
      </Stack>
    </Box>
  );
};

export default VocabularyTestProgressCard;
